/* import __COLOCATED_TEMPLATE__ from './teammate-removal-modal.hbs'; */
/* RESPONSIBLE TEAM: team-self-serve */
import type AppService from 'embercom/services/app-service';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { modifier } from 'ember-modifier';
import type IntlService from 'embercom/services/intl';
import type Admin from 'embercom/models/admin';
import type NotificationsService from 'embercom/services/notifications-service';
import type Permission from 'embercom/models/permission';
import { post } from 'embercom/lib/ajax';
import { PRICING_5_X_CORE_ESSENTIAL_ID } from 'embercom/lib/billing';
export interface Args {
  onClose: (didRemoveTeammates?: boolean) => void;
  selectedPlanId?: string;
}

interface Signature {
  Element: HTMLDivElement;
  Args: Args;
}

export default class TeammateRemovalModal extends Component<Signature> {
  @service declare appService: AppService;
  @service declare intl: IntlService;
  @service declare notificationsService: InstanceType<typeof NotificationsService>;
  @tracked selectedAdminsForRemoval: string[] = [];
  @tracked removalInProgress = false;
  @tracked showConfirmation = false;

  syncCheckboxState = modifier(
    (element: HTMLInputElement, [checked, indeterminate]: [boolean, boolean]) => {
      element.checked = checked;
      element.indeterminate = indeterminate;
    },
  );

  syncAdminCheckbox = modifier(
    (element: HTMLInputElement, [selectedIds, adminId]: [string[], string]) => {
      element.checked = selectedIds.includes(adminId);
    },
  );

  get currentAdmin() {
    return this.appService.app.currentAdmin;
  }

  get relevantAdmins() {
    let hasAssignedCoreSeats = this.appService.app.humanAdminsWithCoreSeat.length;
    let viewingEssential = this.args.selectedPlanId === PRICING_5_X_CORE_ESSENTIAL_ID;
    if (hasAssignedCoreSeats && !viewingEssential) {
      return this.appService.app.humanAdminsWithCoreSeat;
    }
    return this.appService.app.humanAdmins;
  }

  get adminsForDisplay() {
    let currentId = this.currentAdmin.id;
    return [...this.relevantAdmins].sort((a: Admin, b: Admin) => {
      if (a.id === currentId) {
        return -1;
      }
      if (b.id === currentId) {
        return 1;
      }
      return 0;
    });
  }

  get selectableAdmins() {
    return this.adminsForDisplay.filter((admin: Admin) => admin.id !== this.currentAdmin.id);
  }

  get allSelected() {
    return this.selectedAdminsForRemoval.length === this.selectableAdmins.length;
  }

  get isIndeterminate() {
    return (
      this.selectedAdminsForRemoval.length > 0 &&
      this.selectedAdminsForRemoval.length < this.selectableAdmins.length
    );
  }

  @action handleModalClose() {
    this.args.onClose(false);
  }

  @action toggleAdminForRemoval(admin: Admin) {
    if (this.selectedAdminsForRemoval.includes(admin.id)) {
      this.selectedAdminsForRemoval = this.selectedAdminsForRemoval.filter((id) => id !== admin.id);
    } else {
      this.selectedAdminsForRemoval = [...this.selectedAdminsForRemoval, admin.id];
    }
  }

  @action toggleAllTeammatesForRemoval() {
    if (this.selectedAdminsForRemoval.length < this.selectableAdmins.length) {
      this.selectedAdminsForRemoval = this.selectableAdmins.map((admin: Admin) => admin.id);
    } else {
      this.selectedAdminsForRemoval = [];
    }
  }

  @action showConfirmationView() {
    this.showConfirmation = true;
  }

  @action goBackToSelection() {
    this.showConfirmation = false;
  }

  @action async executeRemoval() {
    this.removalInProgress = true;
    let res = await this.removeSelectedTeammates();
    if (res.success) {
      this.showSuccessNotification();
      // Reload app model before closing so the parent sees updated admin data
      await this.appService.app.reload();
      this.removalInProgress = false;
      this.args.onClose(true);
    } else {
      this.showErrorNotification(res.error || '');
      this.removalInProgress = false;
    }
  }

  getPermissionIdsForRemoval(): Array<string> {
    return this.selectedAdminsForRemoval
      .map((adminId) => {
        let admin = this.adminsForDisplay.find((a: Admin) => a.id === adminId);
        let permission = admin?.app_permissions?.find(
          (permission: Permission) => permission.app_id === this.appService.app.id,
        );
        return permission?.id;
      })
      .filter(Boolean);
  }

  async removeSelectedTeammates(): Promise<{ success: boolean; error?: string }> {
    let permissionIds = this.getPermissionIdsForRemoval();
    try {
      await post('/ember/permissions/bulk_delete', {
        app_id: this.appService.app.id,
        permission_ids: permissionIds,
        reassign_to_admin_id: this.currentAdmin.id,
      });
      return { success: true };
    } catch (e) {
      console.error(e, e.message);
      return {
        success: false,
        error: e.message || e.errorThrown,
      };
    }
  }

  showSuccessNotification() {
    let removedAdmins = this.selectedAdminsForRemoval.map((adminId: string) =>
      this.adminsForDisplay.find((a: Admin) => a.id === adminId),
    );
    let adminCount = removedAdmins.length;
    let firstAdminName = '';
    if (adminCount === 1) {
      // Show an alert right now without the actual logic
      let firstSelectedId = this.selectedAdminsForRemoval[0];
      let firstSelectedAdmin = firstSelectedId
        ? this.adminsForDisplay.find((a: Admin) => a.id === firstSelectedId)
        : undefined;

      firstAdminName = firstSelectedAdmin.name;
    }

    this.notificationsService.notifyConfirmation(
      this.intl.t(
        'signup.teams.pricing5.annual-plans.configure-plan.teammate-removal-modal.removal-success',
        {
          removedAdmins: this.selectedAdminsForRemoval.length,
          removedAdminName: firstAdminName,
        },
      ),
    );
  }

  showErrorNotification(errorMessage: string) {
    this.notificationsService.notifyError(
      this.intl.t(
        'signup.teams.pricing5.annual-plans.configure-plan.teammate-removal-modal.removal-error',
        {
          errorMessage,
        },
      ),
    );
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Signup::Teams::Pricing5::Components::TeammateRemovalModal': typeof TeammateRemovalModal;
  }
}
